/** Beta Discovery client — operator plane (Firebase) + guest plane (token/OTP). */

import { authHeaders } from './auth'

const base =
  (import.meta.env.VITE_TASKS_API_BASE as string | undefined)?.replace(/\/$/, '') ??
  ''

export class DiscoveryApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'DiscoveryApiError'
    this.status = status
  }
}

async function req<T>(
  path: string,
  init?: RequestInit,
  authed = true,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(authed ? ((await authHeaders()) as Record<string, string>) : {}),
  }
  const res = await fetch(`${base}${path}`, { ...init, headers })
  if (!res.ok) {
    const text = await res.text()
    throw new DiscoveryApiError(res.status, text || `${res.status}`)
  }
  return (await res.json()) as T
}

export type DiscoveryTemplateMeta = {
  id: string
  title: string
  lede: string
  field_count: number
}

export type DiscoveryFormMeta = {
  id: string
  title: string
  status: 'draft' | 'active' | 'completed' | 'revoked' | 'expired'
  owner_email: string | null
  audience_emails: string[]
  json_schema: Record<string, unknown>
  ui_schema: Record<string, unknown>
  gmail_thread_id: string | null
  expires_at: string | null
  created_at: string | null
  answer_count: number
}

export type DiscoveryAnswer = {
  respondent_email: string
  answers: Record<string, unknown>
  submitted_at: string | null
}

export type CreateDiscoveryFormBody = {
  title?: string
  audience_emails: string[]
  example?: string
  json_schema?: Record<string, unknown>
  ui_schema?: Record<string, unknown>
  goal?: string
  missing_fields?: string[]
  template?: string
  expires_hours?: number
}

/** Operator plane — Firebase + allowlist; forms are owner-scoped. */
export const discoveryBeta = {
  templates: () =>
    req<{ templates: DiscoveryTemplateMeta[] }>('/discovery/templates'),
  createForm: (body: CreateDiscoveryFormBody) =>
    req<{ form: DiscoveryFormMeta }>('/discovery/forms', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listForms: () => req<{ forms: DiscoveryFormMeta[] }>('/discovery/forms'),
  getForm: (formId: string) =>
    req<{ form: DiscoveryFormMeta; answers: DiscoveryAnswer[] }>(
      `/discovery/forms/${formId}`,
    ),
  activate: (formId: string) =>
    req<{
      form: DiscoveryFormMeta
      guest_path: string
      capability_token: string
      note: string
    }>(`/discovery/forms/${formId}/activate`, { method: 'POST' }),
  revoke: (formId: string) =>
    req<{ form: DiscoveryFormMeta }>(`/discovery/forms/${formId}/revoke`, {
      method: 'POST',
    }),
  /** Activate (fresh token) + email the guest link to the audience via Ellis Gmail. */
  sendLink: (formId: string) =>
    req<{
      form: DiscoveryFormMeta
      sent_to: string[]
      guest_path: string
      guest_url: string
    }>(`/discovery/forms/${formId}/send`, { method: 'POST' }),
}

export type GuestForm = {
  title: string
  status: string
  json_schema: Record<string, unknown>
  ui_schema: Record<string, unknown>
  expires_at: string | null
}

/** Guest plane — no Firebase; thread parties prove inbox via OTP. */
export const discoveryGuest = {
  resolve: (token: string) =>
    req<{ form: GuestForm }>(
      `/discovery/guest/resolve?token=${encodeURIComponent(token)}`,
      undefined,
      false,
    ),
  requestOtp: (token: string, email: string) =>
    req<{ ok: boolean; email: string; dev_otp_code?: string; note?: string }>(
      '/discovery/guest/otp',
      { method: 'POST', body: JSON.stringify({ token, email }) },
      false,
    ),
  openSession: (token: string, email: string, code: string) =>
    req<{ session_token: string; email: string }>(
      '/discovery/guest/session',
      { method: 'POST', body: JSON.stringify({ token, email, code }) },
      false,
    ),
  submit: (session_token: string, answers: Record<string, unknown>) =>
    req<{ status: string; respondent_email: string; submitted_at: string | null }>(
      '/discovery/guest/submit',
      { method: 'POST', body: JSON.stringify({ session_token, answers }) },
      false,
    ),
}
